import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import api from '../../services/api';
import type { AiCreditsStatement } from '../../services/adminService';
import { useAiCredits } from '../../contexts/AiCreditsContext';
import { reais, creditos } from '../../services/aiCreditsService';
import { rotuloAcao, rotuloFuncionalidade } from '../../utils/aiFeatureLabels';

const tokens = (n: number) => n.toLocaleString('pt-BR');

/**
 * Uso de IA da conta (Configurações): o que o plano dá no mês e quanto resta, o saldo comprado,
 * o gasto por mês e por funcionalidade, a média de cada ação e as automações que mais gastam.
 * A equipe vê o consumo da conta do dono, que é de onde sai o saldo.
 */
const AiUsageSettings: React.FC = () => {
  const { t } = useTranslation();
  const { openRecharge, summary: resumoAoVivo } = useAiCredits();
  const [dados, setDados] = useState<AiCreditsStatement | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    api.get('/ai-billing/me')
      .then((res) => { if (res.data?.success) setDados(res.data.data); else setErro(true); })
      .catch(() => setErro(true));
  }, []);

  if (erro) return <p className="text-sm text-gray-500 dark:text-gray-400">{t('errors.generic')}</p>;
  if (!dados) return <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  // O resumo do contexto atualiza sozinho depois de uma recarga; o da página é o da abertura
  const resumo = resumoAoVivo || dados.summary;
  const mes = dados.months[0];
  const doMes = dados.features.filter((f) => f.month === mes?.month);
  const medias = [...(dados.averages || [])].filter((a) => a.action).sort((a, b) => b.avg_cents - a.avg_cents);
  const automacoes = dados.top_automations || [];

  const cartao = (rotulo: string, valor: string, extra?: React.ReactNode, vermelho = false) => (
    <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{rotulo}</p>
      <p className={`text-xl font-bold ${vermelho ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{valor}</p>
      {extra}
    </div>
  );

  return (
    <div className="space-y-3 sm:space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>{t('settings.aiUsage.title', 'Uso de IA')}</CardTitle>
            {!resumo?.postpaid && (
              <button type="button" onClick={openRecharge} className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700">
                {t('aiCredits.recharge', 'Recarregar')}
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!dados.billing_active && (
            <p className="text-xs rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 px-3 py-2">
              {t('settings.aiUsage.shadow', 'A partir de {{data}} o uso de IA passa a ser descontado dos créditos da conta. Abaixo está o consumo registrado até agora.', { data: new Date(dados.billing_start).toLocaleDateString('pt-BR') })}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('settings.aiUsage.how', 'Tudo que usa IA consome créditos (1 crédito = R$ 0,01), conforme os tokens usados. O plano dá uma franquia por mês, que renova no dia 1º; o que você compra soma a ela e não vence. O gasto sai primeiro da franquia.')}
          </p>
          {resumo && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {cartao(t('aiCredits.available', 'Disponível'), reais(resumo.available_cents), resumo.postpaid
                ? <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.aiUsage.postpaid', 'Conta pós-paga: o consumo do mês vem na fatura')}</p>
                : null, !resumo.postpaid && resumo.available_cents <= 0)}
              {cartao(t('aiCredits.planMonth', 'Do plano no mês'), `${reais(resumo.included_remaining_cents)} / ${reais(resumo.monthly_allowance_cents)}`)}
              {cartao(t('aiCredits.purchased', 'Comprado'), reais(resumo.purchased_balance_cents), null, resumo.purchased_balance_cents < 0)}
              {cartao(t('settings.aiUsage.thisMonth', 'Consumo no mês'), reais(resumo.month_spent_cents),
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.aiUsage.monthDetail', '{{usos}} usos · {{tokens}} tokens', { usos: tokens(resumo.month_calls), tokens: tokens(resumo.month_tokens) })}</p>)}
            </div>
          )}

          {doMes.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settings.aiUsage.byFeature', 'Onde a IA foi usada em {{mes}}', { mes: mes?.month })}</p>
              <ul className="text-sm space-y-0.5">
                {doMes.map((f) => (
                  <li key={f.feature} className="flex justify-between gap-3 text-gray-700 dark:text-gray-300">
                    <span>{rotuloFuncionalidade(f.feature)}</span>
                    <span className="text-right">{reais(f.value_cents)} <span className="text-xs text-gray-400">({tokens(f.calls)} usos · {tokens(f.tokens)} tokens)</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {medias.length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settings.aiUsage.averages', 'Média por uso de cada ação (últimos 30 dias)')}</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">{t('settings.aiUsage.action', 'Ação')}</th>
                    <th className="py-2 pr-3 text-right">{t('settings.aiUsage.calls', 'Usos')}</th>
                    <th className="py-2 pr-3 text-right">{t('settings.aiUsage.avgTokens', 'Tokens por uso')}</th>
                    <th className="py-2 text-right">{t('settings.aiUsage.avgValue', 'Créditos por uso')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {medias.map((m) => (
                    <tr key={`${m.feature}|${m.action}`} className="text-gray-900 dark:text-white">
                      <td className="py-2 pr-3">
                        {rotuloAcao(m.feature, m.action)}
                        {m.source !== 'account' && <span className="ml-1 text-xs text-gray-400">{t('settings.aiUsage.globalAvg', '(média geral)')}</span>}
                      </td>
                      <td className="py-2 pr-3 text-right">{tokens(m.uses)}</td>
                      <td className="py-2 pr-3 text-right">{tokens(m.avg_tokens)}</td>
                      <td className="py-2 text-right">{creditos(m.avg_cents)} <span className="text-xs text-gray-400">({reais(m.avg_cents)})</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {automacoes.length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settings.aiUsage.automations', 'Automações que mais gastaram (últimos 30 dias)')}</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">{t('settings.aiUsage.automation', 'Automação')}</th>
                    <th className="py-2 pr-3 text-right">{t('settings.aiUsage.runs', 'Execuções')}</th>
                    <th className="py-2 pr-3 text-right">{t('settings.aiUsage.perRun', 'Por execução')}</th>
                    <th className="py-2 text-right">{t('settings.aiUsage.value', 'Valor')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {automacoes.map((a) => (
                    <tr key={a.workflow_id} className="text-gray-900 dark:text-white">
                      <td className="py-2 pr-3">{a.name || t('settings.aiUsage.deletedAutomation', 'Automação removida')}</td>
                      <td className="py-2 pr-3 text-right">{tokens(a.runs)}</td>
                      <td className="py-2 pr-3 text-right">{reais(a.avg_cents_per_run)}</td>
                      <td className="py-2 text-right">{reais(a.total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {dados.months.length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settings.aiUsage.byMonth', 'Consumo por mês')}</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">{t('settings.aiUsage.month', 'Mês')}</th>
                    <th className="py-2 pr-3 text-right">{t('settings.aiUsage.calls', 'Usos')}</th>
                    <th className="py-2 pr-3 text-right">{t('settings.aiUsage.tokens', 'Tokens')}</th>
                    <th className="py-2 text-right">{t('settings.aiUsage.value', 'Valor')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dados.months.map((m) => (
                    <tr key={m.month} className="text-gray-900 dark:text-white">
                      <td className="py-2 pr-3">{m.month}</td>
                      <td className="py-2 pr-3 text-right">{tokens(m.calls)}</td>
                      <td className="py-2 pr-3 text-right">{tokens(m.input_tokens + m.output_tokens)}</td>
                      <td className="py-2 text-right">{reais(m.value_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AiUsageSettings;
